import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  SelectChangeEvent,
  Snackbar,
  Alert,
} from "@mui/material";
import { Product, ProductWrite } from "utils/types/ProductTypes";
import {
  createProduct,
  createProvider,
  getAllProviders,
  updateProduct,
} from "utils/api/private/ProductApiService";
import { extractErrorMessage, Severity } from "utils/helper/ApiResponses";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  product: Product | null;
}

// FIX: Added priceUSD to the empty state to satisfy the Product type and prevent errors.
const emptyProduct: ProductWrite = {
  name: "",
  provider: "",
  category: "",
  price: 0,
  currency: "",
  country: "",
  countryCode: "",
  isBestseller: false,
  image: "",
};
export const ProductDialog = ({ open, onClose, onSave, product }: Props) => {
  const [formData, setFormData] = useState<ProductWrite>(emptyProduct);
  const [providers, setProviders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddingNewProvider, setIsAddingNewProvider] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: Severity;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  useEffect(() => {
    // Fetch providers when the component mounts
    const fetchProviders = async () => {
      try {
        const fetchedProviders = await getAllProviders();
        setProviders(fetchedProviders.map((p) => p.name));
      } catch (error) {
        console.error("Failed to fetch providers:", error);
      }
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        provider: product.provider,
        category: product.category,
        price: Number(product.price) || 0,
        currency: product.currency,
        country: product.country,
        countryCode: product.countryCode,
        isBestseller: !!product.isBestseller,
        image: product.image,
      });
    } else {
      setFormData(emptyProduct);
    }
    setIsAddingNewProvider(false);
    setNewProviderName("");
  }, [product, open]);

  const numericFields = new Set(["price"]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    const key = name as keyof typeof formData;
    setFormData((prev) => ({
      ...prev,
      [key]: numericFields.has(String(key))
        ? Number(value) // number
        : (value as any), // string/boolean
    }));
  };

  const handleProviderChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    if (value === "_new_") {
      setIsAddingNewProvider(true);
      setFormData((prev) => ({ ...prev, provider: "" }));
    } else {
      setIsAddingNewProvider(false);
      setFormData((prev) => ({ ...prev, provider: value }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, isBestseller: e.target.checked }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      console.log("Form Data to be saved:", formData);
      let finalProviderName = formData.provider;
      if (isAddingNewProvider && newProviderName) {
        console.log("Creating new provider:", newProviderName);
        await createProvider({ name: newProviderName, url: formData.image });
        finalProviderName = newProviderName;
      }

      // Build payload WITHOUT priceUSD/pricing (pricing is handled in PricingDialog)
      const { /* strip computed/config fields */ ...rest } = formData;
      const payload = {
        ...rest,
        provider: finalProviderName,
      } as Omit<Product, "id">;

      // Explicitly delete just in case
      delete (payload as any).priceUSD;
      delete (payload as any).pricing;

      if (product?.id) {
        await updateProduct(product.id, payload);
        setToast({
          open: true,
          severity: "success",
          message: "Product updated successfully.",
        });
      } else {
        await createProduct(payload);
        setToast({
          open: true,
          severity: "success",
          message: "Product created successfully.",
        });
      }

      onSave();
      onClose();
    } catch (error) {
      const { message, code } = extractErrorMessage(error);
      console.error("Failed to save product:", error);
      setToast({
        open: true,
        severity: "error",
        message: code ? `${message} (code ${code})` : message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{product ? "Edit Product" : "Add New Product"}</DialogTitle>
      <DialogContent>
        <TextField
          name="name"
          label="Product Name"
          value={formData.name}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Provider</InputLabel>
          <Select
            name="provider"
            value={isAddingNewProvider ? "_new_" : formData.provider}
            label="Provider"
            onChange={handleProviderChange}
          >
            {providers.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
            <MenuItem value="_new_">Add New Provider...</MenuItem>
          </Select>
        </FormControl>
        {isAddingNewProvider && (
          <TextField
            label="New Provider Name"
            value={newProviderName}
            onChange={(e) => setNewProviderName(e.target.value)}
            fullWidth
            margin="dense"
          />
        )}
        <TextField
          name="category"
          label="Category"
          value={formData.category}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          name="price"
          label="Price (Local Currency)"
          type="number"
          value={formData.price}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          name="currency"
          label="Currency Symbol (e.g., €, $)"
          value={formData.currency}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          name="country"
          label="Country Name"
          value={formData.country}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <TextField
          name="countryCode"
          label="Country Code (e.g., US, FR)"
          value={formData.countryCode}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        {/* REMOVE: priceUSD input (computed) */}
        {/* <TextField name="priceUSD" ... /> */}

        <TextField
          name="image"
          label="Image URL"
          value={formData.image}
          onChange={handleChange}
          fullWidth
          margin="dense"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isBestseller}
              onChange={handleCheckboxChange}
            />
          }
          label="Bestseller"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={closeToast}
          severity={toast.severity}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
